<template>
  <div class="wheel-wrapper" ref="wrapperRef" role="spinbutton" tabindex="0" :aria-valuemin="items[0]" :aria-valuemax="items.at(-1)" :aria-valuenow="modelValue" :aria-valuetext="formatter ? formatter(modelValue) : String(modelValue).padStart(2, '0')" @keydown="onKey">
    <div class="wheel-highlight" />
    <div class="wheel-scroll" ref="scrollRef" @scroll="onScroll">
      <div class="wheel-pad" />
      <div
        v-for="(item, idx) in items"
        :key="idx"
        class="wheel-item"
        :class="{ active: item === modelValue }"
      >{{ formatter ? formatter(item) : String(item).padStart(2, '0') }}</div>
      <div class="wheel-pad" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'

const ITEM_H = 40

const props = defineProps({
  items:      { type: Array, required: true },
  modelValue: { type: Number, default: 0 },
  formatter:  { type: Function, default: null },
})

const emit = defineEmits(['update:modelValue'])

function onKey(event) {
  const index = props.items.indexOf(props.modelValue)
  let next
  if (event.key === 'ArrowUp') next = Math.min(props.items.length - 1, index + 1)
  else if (event.key === 'ArrowDown') next = Math.max(0, index - 1)
  else if (event.key === 'Home') next = 0
  else if (event.key === 'End') next = props.items.length - 1
  else return
  event.preventDefault()
  emit('update:modelValue', props.items[next])
}
const scrollRef  = ref(null)
const wrapperRef = ref(null)
let skipEmit = false
let releaseTimer = null

function scrollToValue(val, smooth = false) {
  const idx = props.items.indexOf(val)
  if (idx < 0 || !scrollRef.value) return
  clearTimeout(releaseTimer)
  clearTimeout(scrollTimer)
  skipEmit = true
  const animate = smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  scrollRef.value.scrollTo({
    top: idx * ITEM_H,
    behavior: animate ? 'smooth' : 'instant',
  })
  releaseTimer = setTimeout(() => { skipEmit = false }, animate ? 350 : 80)
}

let scrollTimer = null
function onScroll() {
  clearTimeout(scrollTimer)
  scrollTimer = setTimeout(() => {
    if (!scrollRef.value || skipEmit) return
    const idx = Math.round(scrollRef.value.scrollTop / ITEM_H)
    const clamped = Math.max(0, Math.min(idx, props.items.length - 1))
    if (props.items[clamped] !== props.modelValue) {
      emit('update:modelValue', props.items[clamped])
    }
  }, 80)
}

onBeforeUnmount(() => { clearTimeout(releaseTimer); clearTimeout(scrollTimer) })

onMounted(() => {
  nextTick(() => scrollToValue(props.modelValue))
})

watch(() => props.modelValue, (val) => {
  scrollToValue(val, true)
})
</script>

<style scoped>
.wheel-wrapper {
  position: relative;
  height: 120px;
  width: 52px;
  overflow: hidden;
}

.wheel-highlight {
  position: absolute;
  top: 40px;
  left: 0;
  right: 0;
  height: 40px;
  background: rgba(65, 41, 26, 0.08);
  border-radius: 8px;
  pointer-events: none;
  z-index: 1;
}

.wheel-scroll {
  height: 120px;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.wheel-scroll::-webkit-scrollbar { display: none; }

.wheel-pad {
  height: 40px;
  scroll-snap-align: start;
}

.wheel-item {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Inter, sans-serif;
  font-size: var(--fs-20);
  font-weight: 400;
  color: var(--ink-70);
  scroll-snap-align: start;
  transition: color 0.15s, font-weight 0.15s;
  user-select: none;
}

.wheel-item.active {
  color: var(--ink);
  font-weight: 600;
}
</style>
